import { Inject, Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Params,
    Router,
    RouterStateSnapshot,
} from "@angular/router";
import { combineLatest, Observable, of } from "rxjs";
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import {
    CityEntry,
    ContinentEntry,
    CountryEntry,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_COUNTRY_MODE,
    SHOP_LOCATIONS_DEFAULT_MODE,
    ShopLocationsFacade,
    ShopLocationsPageMode,
} from "@hermes/states/shop-locations";
import { ShopLocatorFacade } from "@hermes/states/shop-locator";

@Injectable()
export class ShopLocatorPageResolver {
    constructor(
        private shopLocatorFacade: ShopLocatorFacade,
        private shopLocationsFacade: ShopLocationsFacade,
        private router: Router,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<CityEntry | undefined> {
        this.shopLocatorFacade.resetShopLocator();

        this.shopLocatorFacade.fetchShops({
            lang: this.locale.langCode,
        });

        const mode = this.getMode(route.params);

        /**
         * Create a flux that combine events from :
         * * shops are loaded
         * * shops are loaded with errors.
         */
        return combineLatest([
            this.shopLocatorFacade.shopsLoaded$,
            this.shopLocatorFacade.shopsHasError$,
        ]).pipe(
            withLatestFrom(this.shopLocatorFacade.isLoadedWithoutShops$),
            map(([[shopsLoaded, shopsHasError], isLoadedWithoutShops]) => {
                if (shopsHasError || isLoadedWithoutShops) {
                    throw new Error("Error during loading");
                }
                return shopsLoaded;
            }),
            filter((status) => status),
            tap(() => this.shopLocationsFacade.generateCountriesFromShops()),
            switchMap(() =>
                this.countryModeManagement(mode, route.params["country"]),
            ),
            switchMap(() =>
                this.cityModeManagement(mode, route.params["city"]),
            ),
            tap(() => this.shopLocationsFacade.updateShopLocator()),
            take(1),
            catchError(() => {
                this.router.navigate(["/404-error"], {
                    skipLocationChange: true,
                    queryParams: {
                        originalUrl: state.url,
                    },
                });
                return of(undefined);
            }),
        );
    }

    private countryModeManagement(
        mode: ShopLocationsPageMode,
        countryInParameter: string,
    ) {
        return this.shopLocationsFacade.hasCountries$.pipe(
            filter((status) => status),
            switchMap(() => this.shopLocationsFacade.countries$),
            map((countries: CountryEntry[]) => {
                this.setContinentsInState(countries);

                const countryCode: string | undefined = [
                    SHOP_LOCATIONS_CITY_MODE,
                    SHOP_LOCATIONS_COUNTRY_MODE,
                ].includes(mode)
                    ? this.getCountryCodeFromUrl(countries, countryInParameter)
                    : this.locale.countryCode;
                if (!countryCode) {
                    throw new Error("Invalid country");
                }
                this.shopLocationsFacade.setCurrentCountry(
                    countryCode,
                    [
                        SHOP_LOCATIONS_CITY_MODE,
                        SHOP_LOCATIONS_COUNTRY_MODE,
                    ].includes(mode)
                        ? SHOP_LOCATIONS_COUNTRY_MODE
                        : SHOP_LOCATIONS_DEFAULT_MODE,
                );
                return countryCode;
            }),
        );
    }

    private setContinentsInState(countries: CountryEntry[]) {
        const continentsEntriesMappedByName = new Map<string, ContinentEntry>();

        for (const country of countries) {
            if (!continentsEntriesMappedByName.has(country.continentName)) {
                continentsEntriesMappedByName.set(country.continentName, {
                    name: country.continentName,
                    closed: true,
                    countries: [],
                });
            }

            continentsEntriesMappedByName
                .get(country.continentName)
                ?.countries.push(country);
        }

        const alphabeticallySortedContinents = [
            ...continentsEntriesMappedByName.values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        this.shopLocationsFacade.setContinents(alphabeticallySortedContinents);
    }

    private cityModeManagement(
        mode: ShopLocationsPageMode,
        cityInParameter: string,
    ) {
        if (mode !== SHOP_LOCATIONS_CITY_MODE) {
            return of(undefined);
        }
        return this.shopLocationsFacade.hasCities$.pipe(
            filter((status) => status),
            switchMap(() => this.shopLocationsFacade.cities$),
            map((cities: CityEntry[]) => {
                const city = this.getCityEntryFromUrl(cities, cityInParameter);
                if (!city) {
                    throw new Error("Invalid city");
                }
                this.shopLocationsFacade.setCurrentCity(city);
                return city;
            }),
        );
    }

    private getCountryCodeFromUrl(
        countries: CountryEntry[],
        slugifiedCountry: string,
    ): string | undefined {
        return countries.find(
            (entry) => entry.slugifiedCountry === slugifiedCountry,
        )?.countryCode;
    }

    private getCityEntryFromUrl(
        cities: CityEntry[],
        slugifiedCity: string,
    ): CityEntry | undefined {
        return cities.find((entry) => entry.slugifiedCity === slugifiedCity);
    }

    private getMode(params: Params): ShopLocationsPageMode {
        if (params["city"] || params["country"]) {
            return params["city"] && params["country"]
                ? SHOP_LOCATIONS_CITY_MODE
                : SHOP_LOCATIONS_COUNTRY_MODE;
        }
        return SHOP_LOCATIONS_DEFAULT_MODE;
    }
}
